import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { provide } from '@lit/context'
import { agentContext } from './contexts/agent'
import { breakpointContext } from './contexts/breakpoint'
import { shopperContext, type ShopperContextType } from './contexts/shopper'
import { BREAKPOINT, getBreakpoint } from './util/breakpoints'
import { AepService } from './services/aep.service'
import { QuoteService } from './services/quote-service'
import './components/layout/footer'
import './components/form/form'
import './components/pages/vision-results'
import './components/pages/dental-results'
import './components/shared/agent-banner'
import './components/shared/producer-email-entry'
import './util/tooltip'
import type { Producer } from './util/api'
import { AGENT_PORTAL_DASH_URL } from './util/api'
import { getBasePath, withBase } from './util/base-path'
import {
  isReloadNavigation,
  navigateReplace,
  navigatePush,
  maybeRedirectToIndexHtmlIfReload,
} from './util/navigation'
import {
  hasDentalPlans,
  collectAgentRecommendedPlans,
  syncRecommendedFlagsFromEmailResult,
} from './util/recommendations'
import { clearSelections } from './util/clear-selections'
import { getEmbeddedContext, isEmbeddedFlow } from './util/embedded'
import { EVENT } from './util/events'
import { ROUTE } from './util/routes'
import { STORAGE_KEY } from './util/storage-keys'
const bcbsncUrl =
  typeof import.meta.env.VITE_BCBSNC_URL !== 'undefined' &&
  import.meta.env.VITE_BCBSNC_URL
    ? import.meta.env.VITE_BCBSNC_URL
    : 'https://example.com'
const MEMBER_URL = 'https://member.bcbsnc.com/blueconnect/web/login'
const BUYONLINE_DASH_URL = `${bcbsncUrl}/sapps/buyonline/application/OnlineApplication.action`
// Early redirect guard for hard refresh on results pages (shopperstage)
// If the browser loads the app directly at vision/dental results, redirect to the start form
// try {
//   const blocked = [withBase('vision-result'), withBase('dental-result')]
//   const isReload = (() => {
//     try {
//       const entries = performance.getEntriesByType(
//         'navigation'
//       ) as PerformanceNavigationTiming[]
//       if (entries && entries.length > 0) return entries[0].type === 'reload'
//       const legacy = (performance as any).navigation
//       return legacy && legacy.type === 1
//     } catch {
//       return false
//     }
//   })()
//   const hasQuoteId = new URLSearchParams(window.location.search).has('quoteId')
//   if (isReload && blocked.includes(window.location.pathname) && !hasQuoteId) {
//     const indexPath = withBase('index.html')
//     window.history.replaceState(
//       {},
//       '',
//       indexPath + window.location.search + window.location.hash
//     )
//     window.dispatchEvent(new PopStateEvent('popstate'))
//   }
// } catch {
//   // no-op
// }

@customElement('ancillary-rate-quote-container')
export class AncillaryRateQuoteContainer extends LitElement {
  constructor() {
    super()
    // Do not force-redirect based on pathname. This app may be hosted under
    // a subpath (e.g., /dental-vision/). Let the router handle
    // navigation without replacing the location to '/'.
    this.handleResize = this.handleResize.bind(this)
    this.handleFormDataUpdate = this.handleFormDataUpdate.bind(this)
    this.handleQuoteDataUpdate = this.handleQuoteDataUpdate.bind(this)
    this.handlePopState = this.handlePopState.bind(this)
  }

  private basePath: string = getBasePath()

  connectedCallback() {
    super.connectedCallback()

    // Set up event listeners FIRST - before any early returns
    window.addEventListener(EVENT.RESIZE, this.handleResize)
    this.handleResize() // Set initial breakpoint

    // Listen for form data updates on document to catch events from router outlet
    document.addEventListener(EVENT.UPDATE_FORM_DATA, this.handleFormDataUpdate)
    // Listen for quote data updates on document to catch events from router outlet
    document.addEventListener(
      EVENT.QUOTE_DATA_UPDATED,
      this.handleQuoteDataUpdate
    )
    // Listen for history changes to re-render current route
    window.addEventListener(EVENT.POPSTATE, this.handlePopState)
    // Listen for custom navigation events (since pushState doesn't trigger popstate)
    window.addEventListener('navigate', this.handlePopState as EventListener)
    document.addEventListener(
      'route-change',
      this.handlePopState as EventListener
    )

    // Call AEP check on component initialization
    this.initAepCheck()

    const search = window.location.search
    if (search && search.length > 1) {
      const urlParams = new URLSearchParams(search)

      // Check for immediate-binder view parameter
      const viewParam = urlParams.get('view') || ''
      if (viewParam === 'immediate-binder') {
        // if (sessionStorage.getItem('binderProcessed') === 'Y') {
        //   window.location.replace('IBinderPayment_processed.action')
        //   return
        // }

        // window.onload = () => {
        //   const waitEl = document.getElementById('wait')
        //   const binderContentEl = document.getElementById('binderContent')
        //   if (waitEl) waitEl.style.display = 'none'
        //   if (binderContentEl) binderContentEl.style.display = 'block'
        // }

        this.isImmediateBinder = true

        // Navigate immediately to prevent form page from showing
        // Default to dental-result, will be updated based on params after data loads
        const currentPath = window.location.pathname
        const dentalParam = urlParams.get('dental')
        const visionParam = urlParams.get('vision')
        const showDental = dentalParam !== 'false'
        const showVision = visionParam !== 'false'

        // Determine initial page to show
        let initialPath: string = ROUTE.DENTAL_RESULT
        if (!showDental && showVision) {
          initialPath = ROUTE.VISION_RESULT
        } else if (showDental && !showVision) {
          initialPath = ROUTE.DENTAL_RESULT
        } else {
          // Both or neither specified - default to dental
          initialPath = ROUTE.DENTAL_RESULT
        }

        // Only navigate if not already on a results page
        if (
          !currentPath.includes('dental-result') &&
          !currentPath.includes('vision-result')
        ) {
          try {
            navigateReplace(initialPath)
          } catch {
            // no-op
          }
        }

        // Start async data fetch
        this.handleImmediateBinder(urlParams)
        return
      }

      // If `quote` param is present, decode and submit the quote immediately
      // Supports deep-links like: ?quote=<base64-json>&userID=<producer>
      const quoteParam = urlParams.get('quote') || ''
      const hasEncodedQuote = !!quoteParam
      this.hasEncodedQuote = hasEncodedQuote
      if (quoteParam) {
        try {
          const decoded = this.decodeQuoteParam(quoteParam)
          // Ignore `userID` for encoded quote deep-link to ensure non-agent path
          // Build request object, mapping only known fields if present
          const benefitStartDate =
            (decoded.benefitStartDate && String(decoded.benefitStartDate).trim()
              ? decoded.benefitStartDate
              : null) ||
            (decoded.effectiveDate && String(decoded.effectiveDate).trim()
              ? decoded.effectiveDate
              : null) ||
            this.getNextFirstOfMonth()
          const effectiveDate =
            (decoded.effectiveDate && String(decoded.effectiveDate).trim()
              ? decoded.effectiveDate
              : null) ||
            (decoded.benefitStartDate && String(decoded.benefitStartDate).trim()
              ? decoded.benefitStartDate
              : null) ||
            benefitStartDate

          const request: any = {
            offerCrossSale:
              typeof decoded.offerCrossSale === 'boolean'
                ? decoded.offerCrossSale
                : true,
            visionPlan: !!decoded.visionPlan,
            dentalPlan: !!decoded.dentalPlan,
            primaryBirthday: decoded.primaryBirthday || '',
            primaryGender: decoded.primaryGender || '',
            benefitStartDate,
            effectiveDate,
            typeofdental:
              decoded.typeofdental ||
              decoded.typeOfDental ||
              decoded.typeOfDental?.toUpperCase?.() ||
              'INDIVIDUAL',
            applicationType: decoded.applicationType || '',
            defaultApplication: null,
            dependents: Array.isArray(decoded.dependents)
              ? decoded.dependents
              : [],
            // Force non-agent behavior when using encoded quote param, unless deliveryMethod is 'e' for email quote
            isAgent:
              (decoded.deliveryMethod || decoded.deliverMethod || 'e') === 'e',
            email: decoded.email || '',
            firstName: decoded.firstName || '',
            lastName: decoded.lastName || '',
            phone: decoded.phone || '',
            producerPhone: decoded.producerPhone || '',
            producerEmail: decoded.producerEmail || '',
            producerPNumber:
              decoded.producerPNumber || decoded.producerNumber || '',
            fullName: decoded.fullName || '',
            corpName: decoded.corpName || '',
            deliveryMethod:
              decoded.deliveryMethod || decoded.deliverMethod || 'e',
            producerSale: 'y',
            street1: decoded.street1 || '',
            street2: decoded.street2 || '',
            city: decoded.city || '',
            state: decoded.state || '',
            zip: decoded.zip || '',
            countyCode: decoded.countyCode || undefined,
            primarySsn: decoded.primarySsn || '',
          }

          // Pre-route to dental immediately to avoid flashing the start form
          try {
            navigateReplace(ROUTE.DENTAL_RESULT)
          } catch {
            // no-op
          }

          // Submit the quote and store the returned data
          QuoteService.getQuote(request)
            .then((quoteData) => {
              this.shopperContextValue = {
                ...(this.shopperContextValue || {}),
                formData: decoded,
                quoteData,
                isAgent: request.isAgent,
              }
              document.dispatchEvent(
                new CustomEvent('quote-data-updated', { detail: { quoteData } })
              )
              // Navigate directly to results view based on available plans
              try {
                const hasDental = hasDentalPlans(quoteData as any)
                const targetPath = hasDental
                  ? ROUTE.DENTAL_RESULT
                  : ROUTE.VISION_RESULT
                navigateReplace(targetPath)
              } catch {
                // no-op
              }
              this.quoteLoadError = false
            })
            .catch((e) => {
              console.error('Failed to load quote from `quote` param:', e)
              this.quoteLoadError = true
            })
        } catch (e) {
          console.error('Invalid `quote` param; unable to decode/parse:', e)
        }
      }
      // If quoteId is present, fetch the existing quote and bypass the start form
      // Detect email quote from  user email via quoteId param
      const quoteId = urlParams.get('quoteId') || ''
      if (quoteId) {
        // Pre-route to results immediately to avoid flashing the start form
        // Default to dental-first; adjust after quote loads if needed.
        try {
          navigateReplace(ROUTE.DENTAL_RESULT)
        } catch {
          // no-op
        }
        QuoteService.getInitialQuoteById(quoteId)
          .then((quoteData) => {
            // Populate context with fetched quote
            this.shopperContextValue = {
              ...(this.shopperContextValue || {}),
              quoteData,
            }
            // Notify listeners
            document.dispatchEvent(
              new CustomEvent('quote-data-updated', {
                detail: { quoteData },
              })
            )
            // Navigate directly to results view based on available plans
            try {
              const hasDental = hasDentalPlans(quoteData as any)
              const targetPath = hasDental
                ? ROUTE.DENTAL_RESULT
                : ROUTE.VISION_RESULT
              navigateReplace(targetPath)
            } catch {
              // no-op
            }
            this.quoteLoadError = false
          })
          .catch((e) => {
            console.error('Failed to load quote by quoteId:', e)
            this.quoteLoadError = true
          })
      }

      // Detect agent flow (PABOL/EMAIL) via psale=y OR embedded via shared util
      const { psaleIsY, isEmbedded } = getEmbeddedContext()
      const isAgent = psaleIsY || isEmbedded || hasEncodedQuote

      if (isAgent) {
        this.shopperContextValue = {
          ...(this.shopperContextValue || {}),
          isAgent,
        }

        // Always pre-fetch producer session details for agent flows and banner
        QuoteService.getProducerSessionDetails()
          .then((session) => {
            this.shopperContextValue = {
              ...(this.shopperContextValue || {}),
              producerSession: session,
            }
            // After session, pre-fetch initial forms for email flow
            try {
              const pnum =
                (session as any)?.producerNumber ||
                (session as any)?.producerPNumber ||
                ''
              if (pnum) {
                QuoteService.getInitialForms(pnum).catch(() => {
                  // ignore; sendEmail flow can attempt again later
                })
              }
            } catch {
              // no-op
            }
          })
          .catch(() => {
            this.shopperContextValue = {
              ...(this.shopperContextValue || {}),
              producerSession: null,
            }
          })
      }
    }

    // On browser refresh at any route, redirect to explicit index.html under base path
    const hasQuoteId = new URLSearchParams(window.location.search).has(
      'quoteId'
    )
    // Clear session state only on full reload to avoid stale selections, but preserve on Back/Forward
    if (isReloadNavigation()) {
      try {
        clearSelections({ vision: true, dental: true })
      } catch {
        // ignore storage errors
      }
    }
    maybeRedirectToIndexHtmlIfReload(!hasQuoteId && !this.hasEncodedQuote)
  }

  /**
   * Handle immediate-binder flow: fetch vision and dental rates directly
   * and navigate to results pages, skipping the form page
   */
  private async handleImmediateBinder(urlParams: URLSearchParams) {
    try {
      // Get product selection from URL params
      // Dult to true if not specified (backward compatibility)
      const dentalParam = urlParams.get('dental')
      const visionParam = urlParams.get('vision')
      const showDental = dentalParam !== 'false' // Show unless explicitly false
      const showVision = visionParam !== 'false' // Show unless explicitly false

      // Get effective date from URL params or use default.
      // Accept values wrapped in single/double quotes, e.g. '2027-08-01'.
      const rawEffectiveDateParam =
        urlParams.get('effectivedate') || urlParams.get('effectiveDate')
      const effectiveDateParam = rawEffectiveDateParam
        ? rawEffectiveDateParam.trim().replace(/^['\"](.*)['\"]$/, '$1')
        : ''
      const effectiveDate = effectiveDateParam || '2026-05-01'

      const ratesRequest = {
        requestingApplicationId: 'IBCS',
        effectiveDate: effectiveDate,
        household: [
          {
            effectiveDate: effectiveDate,
            applicant: [
              {
                personId: 'Subscriber',
                birthDate: '1980-02-15',
              },
              {
                personId: 'Spouse',
                birthDate: '1990-02-15',
              },
            ],
            householdId: 'soapui',
          },
        ],
      }

      // Fetch only the requested rates based on URL params
      const [visionRates, dentalRates] = await Promise.all([
        showVision
          ? QuoteService.getVisionRates(ratesRequest).catch((e) => {
              console.error('Failed to fetch vision rates:', e)
              return null
            })
          : Promise.resolve(null),
        showDental
          ? QuoteService.getDentalRates(ratesRequest).catch((e) => {
              console.error('Failed to fetch dental rates:', e)
              return null
            })
          : Promise.resolve(null),
      ])

      // Extract plans from either response shape:
      // 1) household[0].visionPlan/dentalPlan
      // 2) top-level visionPlan/dentalPlan
      const extractPlans = (
        ratesResponse: any,
        key: 'visionPlan' | 'dentalPlan'
      ) => {
        const nested = ratesResponse?.household?.[0]?.[key]
        if (Array.isArray(nested)) return nested
        const topLevel = ratesResponse?.[key]
        if (Array.isArray(topLevel)) return topLevel
        return []
      }

      const visionPlans = showVision
        ? extractPlans(visionRates, 'visionPlan')
        : []
      const dentalPlans = showDental
        ? extractPlans(dentalRates, 'dentalPlan')
        : []

      const getImmediateBinderPlanHeader = (plan: any) => {
        const productDescription =
          `${plan?.details?.PRODUCT_DESCRIPTION || ''}`.trim()
        return productDescription || plan.description || ''
      }

      // Transform the rates into the format expected by the results pages
      const quoteData: any = {
        individualPlans: [],
        crossSalePlans: [],
        jsonPlans: [],
        visionPlanQoute: visionPlans.length > 0,
        dentalPlanQoute: dentalPlans.length > 0,
        effectiveDate: effectiveDate,
        quoteApplication: {
          bolApplication: {
            requestedEffectiveDate: effectiveDate,
          },
        },
      }

      // Transform vision rates
      if (visionPlans.length > 0) {
        quoteData.individualPlans = visionPlans.map((plan: any) => {
          // Handle both array and object formats for planRate
          const planRate = Array.isArray(plan.planRate)
            ? plan.planRate[0]
            : plan.planRate

          // Convert string values to numbers if needed
          const parseRate = (value: any) => {
            if (typeof value === 'string') return parseFloat(value) || 0
            return typeof value === 'number' ? value : 0
          }

          const planHeader = getImmediateBinderPlanHeader(plan)

          const transformedPlan = {
            id: plan.id,
            facetsProductId: plan.facetsProductId,
            bluecrossncPartNumber: plan.bluecrossncPartNumber,
            description: planHeader,
            planName: planHeader,
            lob: plan.lob,
            marketSegment: plan.marketSegment,
            details: plan.details,
            planRate: {
              monthlyPremium: parseRate(planRate?.monthlyPremium),
              firstMonthPremium: parseRate(planRate?.firstMonthPremium),
              monthlyResponsibility: parseRate(planRate?.monthlyResponsibility),
              firstMonthResponsibility: parseRate(
                planRate?.firstMonthResponsibility
              ),
            },
            applicantRate: plan.applicantRate || [],
          }

          return transformedPlan
        })
      }

      // Transform dental rates
      if (dentalPlans.length > 0) {
        const transformedDentalPlans = dentalPlans.map((plan: any) => {
          // Handle both array and object formats for planRate
          const planRate = Array.isArray(plan.planRate)
            ? plan.planRate[0]
            : plan.planRate

          // Convert string values to numbers if needed
          const parseRate = (value: any) => {
            if (typeof value === 'string') return parseFloat(value) || 0
            return typeof value === 'number' ? value : 0
          }

          const planHeader = getImmediateBinderPlanHeader(plan)

          const transformedPlan = {
            planId: plan.id,
            facetsProductId: plan.facetsProductId,
            bluecrossncPartNumber: plan.bluecrossncPartNumber,
            description: planHeader,
            planName: planHeader,
            lob: plan.lob,
            marketSegment: plan.marketSegment,
            details: {
              jsonDetails: JSON.stringify(plan.details),
              ...plan.details,
            },
            rate: {
              monthlyPremium: parseRate(planRate?.monthlyPremium),
              firstMonthPremium: parseRate(planRate?.firstMonthPremium),
              monthlyResponsibility: parseRate(planRate?.monthlyResponsibility),
              firstMonthResponsibility: parseRate(
                planRate?.firstMonthResponsibility
              ),
            },
            planRate: plan.planRate,
            applicantRate: plan.applicantRate || [],
          }

          return transformedPlan
        })
        quoteData.crossSalePlans = transformedDentalPlans
        quoteData.jsonPlans = transformedDentalPlans
      }

      // Update shopper context with the fetched data
      // startDate drives SBC/brochure PDF year selection (getPlanYear) for the results pages
      this.shopperContextValue = {
        ...(this.shopperContextValue || {}),
        quoteData,
        isAgent: false,
        formData: {
          ...(this.shopperContextValue?.formData || ({} as any)),
          startDate: effectiveDate,
        },
      }

      // Force a re-render with the new data
      this.requestUpdate()

      // Notify listeners
      document.dispatchEvent(
        new CustomEvent('quote-data-updated', { detail: { quoteData } })
      )

      // Navigate to appropriate results page based on URL params
      const currentPath = window.location.pathname
      const isOnVisionPage = currentPath.includes('vision-result')
      const isOnDentalPage = currentPath.includes('dental-result')

      let targetPath: string = ROUTE.DENTAL_RESULT // default

      // Determine target page based on URL params
      if (showDental && showVision) {
        // Show both: default to dental first, but stay on current page if it has data
        if (isOnVisionPage && visionPlans.length > 0) {
          targetPath = ROUTE.VISION_RESULT
        } else if (isOnDentalPage && dentalPlans.length > 0) {
          targetPath = ROUTE.DENTAL_RESULT
        } else {
          // Navigate to dental first when both are available
          targetPath =
            dentalPlans.length > 0 ? ROUTE.DENTAL_RESULT : ROUTE.VISION_RESULT
        }
      } else if (showVision && !showDental) {
        // Show only vision
        targetPath = ROUTE.VISION_RESULT
      } else if (showDental && !showVision) {
        // Show only dental
        targetPath = ROUTE.DENTAL_RESULT
      } else {
        // Default behavior if params not specified: show dental if available, else vision
        targetPath =
          dentalPlans.length > 0 ? ROUTE.DENTAL_RESULT : ROUTE.VISION_RESULT
      }

      // Only navigate if we're not already there
      if (!currentPath.includes(targetPath)) {
        navigateReplace(targetPath)
      }

      this.quoteLoadError = false
    } catch (e) {
      console.error('Failed to load immediate-binder rates:', e)
      this.quoteLoadError = true
      this.requestUpdate()
    }
  }

  firstUpdated() {
    // Reinforce refresh redirect in case initial router evaluation happened before connectedCallback
    const hasQuoteId2 = new URLSearchParams(window.location.search).has(
      'quoteId'
    )
    maybeRedirectToIndexHtmlIfReload(!hasQuoteId2 && !this.hasEncodedQuote)
  }

  private handleFormDataUpdate(event: Event) {
    const customEvent = event as CustomEvent
    const { formData } = customEvent.detail
    this.shopperContextValue = {
      ...(this.shopperContextValue || {}),
      formData,
    }
  }

  private handleQuoteDataUpdate(event: Event) {
    const customEvent = event as CustomEvent
    const { quoteData } = customEvent.detail
    this.shopperContextValue = {
      ...(this.shopperContextValue || {}),
      quoteData,
    }
    this.requestUpdate()

    // Only navigate from the start form; avoid redirecting when already on results pages.
    try {
      const currentPath = window.location.pathname
      const visionPath = withBase(ROUTE.VISION_RESULT)
      const dentalPath = withBase(ROUTE.DENTAL_RESULT)
      const isResultsRoute =
        currentPath === visionPath || currentPath === dentalPath
      if (isResultsRoute) return

      // Navigate to dental if cross-sale (dental) plans are available; otherwise stay on vision
      const hasDental = hasDentalPlans(quoteData as any)
      const targetPath = hasDental ? ROUTE.DENTAL_RESULT : ROUTE.VISION_RESULT
      // Add a new history entry so browser Back returns to the prior step
      navigatePush(targetPath)
    } catch {
      // no-op
    }
  }

  disconnectedCallback() {
    window.removeEventListener(EVENT.RESIZE, this.handleResize)
    document.removeEventListener(
      EVENT.UPDATE_FORM_DATA,
      this.handleFormDataUpdate
    )
    document.removeEventListener(
      EVENT.QUOTE_DATA_UPDATED,
      this.handleQuoteDataUpdate
    )
    window.removeEventListener(EVENT.POPSTATE, this.handlePopState)
    window.removeEventListener('navigate', this.handlePopState as EventListener)
    document.removeEventListener(
      'route-change',
      this.handlePopState as EventListener
    )
    super.disconnectedCallback()
  }
  // Handle window resize to update breakpoint
  handleResize() {
    const width = window.innerWidth
    const newBreakpoint = getBreakpoint(width)
    if (this.breakpoint !== newBreakpoint) {
      this.breakpoint = newBreakpoint
    }
  }

  async initAepCheck() {
    try {
      const aepResponse = await AepService.doAepCheck()
      this.shopperContextValue = {
        ...this.shopperContextValue,
        aepCheckData: aepResponse,
      }
    } catch (error) {
      console.error('Failed to perform AEP check:', error)
      // Keep aepCheckData as null if the check fails
    }
  }

  private renderVisionResults = () => {
    // If app state is missing, allow direct results when quoteId is present
    if (!this.shopperContextValue.quoteData) {
      const params = new URLSearchParams(window.location.search)
      const hasQuoteId = !!params.get('quoteId')
      // Handle immediate-binder loading state
      if (this.isImmediateBinder) {
        if (this.quoteLoadError) {
          return html`<lh-alert variant="error" inline dismissible>
            Error loading rates. Please try again.
          </lh-alert>`
        }
        return html`<lh-loader></lh-loader>`
      }
      if (hasQuoteId || this.hasEncodedQuote) {
        if (this.quoteLoadError) {
          return this.renderForm()
        }
        return html`<ancillary-vision-results></ancillary-vision-results>`
      }
      if (isReloadNavigation()) {
        return this.redirectToIndexHtml()
      }
      return this.renderForm()
    }
    return html`<ancillary-vision-results></ancillary-vision-results>`
  }

  private renderDentalResults = () => {
    // If app state is missing, allow direct results when quoteId is present
    if (!this.shopperContextValue.quoteData) {
      const params = new URLSearchParams(window.location.search)
      const hasQuoteId = !!params.get('quoteId')
      // Handle immediate-binder loading state
      if (this.isImmediateBinder) {
        if (this.quoteLoadError) {
          return html`<lh-alert variant="error" inline dismissible>
            Error loading rates. Please try again.
          </lh-alert>`
        }
        return html`<lh-loader></lh-loader>`
      }
      if (hasQuoteId || this.hasEncodedQuote) {
        if (this.quoteLoadError) {
          return this.renderForm()
        }
        return html`<ancillary-dental-results></ancillary-dental-results>`
      }
      if (isReloadNavigation()) {
        return this.redirectToIndexHtml()
      }
      return this.renderForm()
    }
    return html`<ancillary-dental-results></ancillary-dental-results>`
  }

  private renderForm = () => {
    // Check for immediate-binder to prevent showing form while loading
    const params = new URLSearchParams(window.location.search)
    const isImmediateBinderParam = params.get('view') === 'immediate-binder'

    if (isImmediateBinderParam) {
      // Show loader instead of form during immediate-binder initialization
      return html`<lh-loader></lh-loader>`
    }

    return html`<ancillary-form></ancillary-form>`
  }

  private redirectToStart = () => {
    // Don't redirect if immediate-binder is detected (already handled in connectedCallback)
    const params = new URLSearchParams(window.location.search)
    const isImmediateBinderParam = params.get('view') === 'immediate-binder'

    if (!isImmediateBinderParam) {
      navigateReplace(ROUTE.START)
    }
    return this.renderForm()
  }

  private redirectToIndexHtml = () => {
    // Don't redirect if immediate-binder is detected (already handled in connectedCallback)
    const params = new URLSearchParams(window.location.search)
    const isImmediateBinderParam = params.get('view') === 'immediate-binder'

    if (!isImmediateBinderParam) {
      navigateReplace('index.html')
    }
    return this.renderForm()
  }

  private renderProducerEmailEntry = () => {
    const currentYear = new Date().getFullYear().toString()
    return html`<rq-producer-email-entry
      .planYear=${currentYear}
      .productionRegion=${import.meta.env.PROD}
      .loading=${this.emailSending}
      .sendMailDone=${this.emailSendDone}
      .errorHtml=${this.emailErrorHtml}
      @send=${this.handleEmailSend}
      @cancel=${this.handleEmailCancel}
      @dashboard=${this.handleEmailDashboard}
    ></rq-producer-email-entry>`
  }

  private getRoutes() {
    return [
      { path: withBase(ROUTE.START), render: this.renderForm },
      {
        path: `${this.basePath}${ROUTE.INDEX}/${ROUTE.START}`,
        render: this.renderForm,
      },
      { path: withBase(ROUTE.VISION_RESULT), render: this.renderVisionResults },
      {
        path: `${this.basePath}${ROUTE.INDEX}/${ROUTE.VISION_RESULT}`,
        render: this.renderVisionResults,
      },
      { path: withBase(ROUTE.DENTAL_RESULT), render: this.renderDentalResults },
      {
        path: `${this.basePath}${ROUTE.INDEX}/${ROUTE.DENTAL_RESULT}`,
        render: this.renderDentalResults,
      },
      // Producer email entry page
      {
        path: withBase(ROUTE.PRODUCER_EMAIL_ENTRY),
        render: this.renderProducerEmailEntry,
      },
      {
        path: `${this.basePath}${ROUTE.INDEX}/${ROUTE.PRODUCER_EMAIL_ENTRY}`,
        render: this.renderProducerEmailEntry,
      },
      // Base path variants
      { path: this.basePath, render: this.redirectToStart },
      {
        path: this.basePath.endsWith('/')
          ? this.basePath.slice(0, -1)
          : this.basePath,
        render: this.redirectToStart,
      },
      { path: `${this.basePath}${ROUTE.INDEX}`, render: this.redirectToStart },
    ]
  }

  private outlet() {
    const pathname = window.location.pathname
    const route = this.getRoutes().find((r) => r.path === pathname)
    return route ? route.render() : this.redirectToStart()
  }
  static styles = css`
    :host {
      margin: 0;
      width: 100vw;
      height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .wrapper {
      background: white;
      padding: 32px;
      border-radius: var(--lh-radii-md, 12px);
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .wrapper.producer-email-entry {
      background: #f8f5f1;
      min-height: 100%;
    }
    .title-button {
      width: 100%;
      display: flex;
      justify-content: end;
    }
    .header-button {
      width: 100%;
      display: flex;
      justify-content: start;
    }
    .header-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: nowrap;
      gap: 8px;
      max-width: 100%;
      min-width: 0;
    }
    .header-actions lh-button {
      white-space: nowrap;
      flex-shrink: 0;
    }
  `
  // Decode a Base64-encoded JSON `quote` param (URL-safe supported)
  private decodeQuoteParam(param: string): any {
    const normalize = (s: string) => {
      const t = s.replace(/-/g, '+').replace(/_/g, '/')
      const pad = t.length % 4 === 0 ? t : t + '='.repeat(4 - (t.length % 4))
      return pad
    }
    const padded = normalize(param.trim())
    const json = atob(padded)
    return JSON.parse(json)
  }

  // Compute next first-of-month in MM/DD/YYYY (used as safe default)
  private getNextFirstOfMonth(): string {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    // if today is before 1st, use current month; else next month
    const nextMonth = now.getDate() === 1 ? month : month + 1
    const adjYear = nextMonth > 12 ? year + 1 : year
    const adjMonth = ((nextMonth - 1) % 12) + 1
    const mm = String(adjMonth).padStart(2, '0')
    const dd = '01'
    const yyyy = String(adjYear)
    return `${mm}/${dd}/${yyyy}`
  }

  @provide({ context: breakpointContext })
  @state()
  breakpoint: BREAKPOINT = BREAKPOINT.LARGE_SCREEN

  @provide({ context: agentContext })
  @property({ type: Object })
  agent?: Producer

  @provide({ context: shopperContext })
  @state()
  shopperContextValue: ShopperContextType = {
    aepCheckData: null,
    quoteData: null,
    isAgent: false,
  }

  // Encoded quote deep-link present in URL
  @state()
  private hasEncodedQuote: boolean = false

  // Producer email entry state
  @state()
  private emailSendDone: 'n' | 'y' = 'n'
  @state()
  private emailSending: boolean = false
  @state()
  private emailErrorHtml: string = ''

  // Quote deep-link error state
  @state()
  private quoteLoadError: boolean = false

  // Immediate binder flow state
  @state()
  private isImmediateBinder: boolean = false

  // Immediate binder exit confirmation modal state
  @state()
  private showExitModal: boolean = false

  get memberLoginButton() {
    if (
      this.breakpoint === BREAKPOINT.PHONE ||
      (this.shopperContextValue?.isAgent && !isEmbeddedFlow())
    )
      return nothing
    return html`<lh-button
      href=${MEMBER_URL}
      variant="outlined"
      icon-position="left"
      icon="user"
      size="md"
      analytics-id="ancillary-rate-quote:member-login:button-outlined"
      >Member Login</lh-button
    >`
  }
  get resumeApplicationButton() {
    if (this.shopperContextValue?.isAgent && !isEmbeddedFlow()) {
      return nothing
    }
    return html`
      <lh-button
        href=${BUYONLINE_DASH_URL}
        variant="filled"
        size="md"
        analytics-id="ancillary-rate-quote:resume-application:button-filled"
        icon-position="left"
        icon=${this.breakpoint !== BREAKPOINT.PHONE
          ? 'clipboard-text'
          : nothing}
        >Resume an Application</lh-button
      >
    `
  }

  private get isProducerEmailEntryRoute() {
    const pathname = window.location.pathname
    return (
      pathname === withBase(ROUTE.PRODUCER_EMAIL_ENTRY) ||
      pathname ===
        `${this.basePath}${ROUTE.INDEX}/${ROUTE.PRODUCER_EMAIL_ENTRY}`
    )
  }

  render() {
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'

    return html`
      <lh-header sticky>
        <lh-logo slot="logo" name="bcbsnc-short"></lh-logo>
        ${isImmediateBinder
          ? html`<lh-typography slot="title" selector="h6" weight="700">
                Completing enrollment
              </lh-typography>
              <div slot="action" class="header-actions">
                <rq-tooltip
                  description="If you experience difficulties, you can also make your first payment via the Blue Cross NC Initial Pay by Phone system available 24/7 at 1-888-802-2386."
                  position="left"
                  hide-line
                  background-color="black"
                  style="--rq-tooltip-width: 350px; --rq-tooltip-max-width: 350px; --rq-tooltip-text-color: white; --lh-tooltip-bottom-spacing: 0px;"
                >
                  <span style="color: var(--lh-color-primary-800)"
                    ><lh-icon name="question" size="sm"></lh-icon
                  ></span>
                </rq-tooltip>
                <div
                  style="border-right:1px solid #BFC3C4; padding-bottom:28px; padding-top:14px;"
                ></div>
                <span style="color: var(--lh-color-primary-800)">
                  <lh-icon
                    name="x"
                    size="sm"
                    style="cursor: pointer;"
                    @click=${() => {
                      this.showExitModal = true
                    }}
                  ></lh-icon>
                </span>
              </div>`
          : !isEmbeddedFlow() &&
              (this.shopperContextValue?.isAgent ||
                this.shopperContextValue?.quoteData?.quoteApplication
                  ?.bolApplication.referralSource === 'PRODUCER_ENTERED')
            ? nothing
            : html`<div slot="action" class="header-actions">
                ${this.memberLoginButton}${this.resumeApplicationButton}
              </div>`}
      </lh-header>
      ${isImmediateBinder && this.showExitModal
        ? html`<lh-modal
            accessible-label="Exit enrollment"
            open
            width="sm"
            @lh-hide=${() => {
              this.showExitModal = false
            }}
          >
            <lh-typography
              type="heading"
              selector="h5"
              weight="400"
              size="lg"
              alignment="center"
            >
              Are you sure you want to exit?
            </lh-typography>
            <div
              style="border-bottom: 1px solid #BEC3CA; margin-top:24px;"
            ></div>
            <lh-typography alignment="left">
              You will be redirected to the Blue Cross North Carolina home page.
            </lh-typography>
            <lh-typography alignment="left">
              Your changes will not be saved, and your policy will not be
              enrolled without payment.
            </lh-typography>
            <lh-button-group alignment="center">
              <lh-button
                type="button"
                @click=${() => {
                  this.showExitModal = false
                }}
              >
                Back
              </lh-button>
              <lh-button
                type="button"
                variant="filled"
                href="https://www.bcbsnc.com/"
                rel="noopener noreferrer"
                >Continue</lh-button
              >
            </lh-button-group>
          </lh-modal>`
        : nothing}
      <rq-agent-banner></rq-agent-banner>
      <lh-container
        theme="neutral-light"
        gutters="none"
        spacing="none"
        content-width="md"
        padding-top="md"
        padding-bottom="md"
      >
        <div
          class=${this.isProducerEmailEntryRoute
            ? 'wrapper producer-email-entry'
            : 'wrapper'}
        >
          <main>${this.outlet()}</main>
        </div>
      </lh-container>
      ${this.isProducerEmailEntryRoute
        ? nothing
        : html`<footer-component></footer-component>`}
    `
  }

  private handlePopState(_event?: Event) {
    // Re-render based on the new URL
    this.requestUpdate()
  }

  private handleEmailCancel = () => {
    const startPath = ROUTE.START
    const start = withBase(startPath)
    if (window.location.pathname !== start) {
      navigatePush(startPath)
    } else {
      this.requestUpdate()
    }
  }

  private handleEmailDashboard = () => {
    try {
      window.location.href =
        typeof AGENT_PORTAL_DASH_URL === 'function'
          ? AGENT_PORTAL_DASH_URL()
          : AGENT_PORTAL_DASH_URL
    } catch {
      // no-op
    }
  }

  private handleEmailSend = async (event: Event) => {
    this.emailErrorHtml = ''
    this.emailSending = true
    try {
      const detail = (event as CustomEvent).detail || {}
      const notice = detail.notice || { ccAgent: false, message: '' }
      const producerNumber =
        (this.shopperContextValue?.producerSession as any)?.producerNumber ||
        (this.shopperContextValue?.producerSession as any)?.producerPNumber ||
        ''
      // Gather agent-recommended plan based on current state
      const current = this.shopperContextValue?.quoteData
      const agentRecommendedPlans = collectAgentRecommendedPlans(current)

      // Check if vision was skipped - if so, remove individualPlans and set visionPlanQoute to false
      // This matches the dental-only behavior
      let objectEncode = current
      try {
        const visionPlanSelected = sessionStorage.getItem(
          STORAGE_KEY.VISION_PLAN_SELECTED
        )
        if (visionPlanSelected === 'false' && current) {
          // Create a copy matching dental-only format when vision is skipped
          const {
            individualPlans: _individualPlans,
            crossSalePlans,
            ...rest
          } = current as any
          objectEncode = {
            ...rest,
            jsonPlans: crossSalePlans, // Move crossSalePlans to jsonPlans (dental-only format)
            visionPlanQoute: false,
          }
        }
      } catch {
        // If sessionStorage access fails, proceed with original data
      }

      // Check if dental was skipped - if so, remove crossSalePlans/jsonPlans and set dentalPlanQoute to false
      // This matches the vision-only behavior
      try {
        const dentalPlanSelected = sessionStorage.getItem(
          STORAGE_KEY.DENTAL_PLAN_SELECTED
        )
        if (dentalPlanSelected === 'false' && objectEncode) {
          // Create a copy removing crossSalePlans and jsonPlans (dental-only format when dental is skipped)
          const {
            crossSalePlans: _crossSalePlans,
            jsonPlans: _jsonPlans,
            ...rest
          } = objectEncode as any
          objectEncode = {
            ...rest,
            dentalPlanQoute: false,
          }
        }
      } catch {
        // If sessionStorage access fails, proceed with original data
      }

      // Collect forms from cache and quote response for email service
      const cached = QuoteService.getCachedAgentForms() || {}
      const quoteForm = current?.forms?.quoteForm
      const emailForms = {
        authorizationToken: (cached as any).authorizationToken,
        producerInfoJ: (cached as any).producerInfoJ,
        quoteForm,
      }

      const result = await QuoteService.sendEmailNotice(
        notice,
        producerNumber,
        agentRecommendedPlans,
        objectEncode,
        emailForms
      )
      // Optionally store result (if needed)
      if (import.meta.env.DEV) {
        console.log(
          '[EmailSend] Result summary:',
          typeof result === 'string'
            ? result.slice(0, 120)
            : {
                hasAgentRecommendedPlans: !!result?.agentRecommendedPlans,
                hasObjectEncode: !!result?.objectEncode,
              }
        )
      }
      // If backend echoes agentRecommendedPlans, sync recommended flags into current quoteData
      const updatedQuote = syncRecommendedFlagsFromEmailResult(
        result,
        this.shopperContextValue?.quoteData
      )
      this.shopperContextValue = {
        ...(this.shopperContextValue || {}),
        quoteData: updatedQuote,
      }
      // Determine success similar to Angular: success if no 'error' present
      let hasError = false
      try {
        if (typeof result === 'string') {
          hasError = /error/i.test(result)
        } else if (result && typeof result === 'object') {
          hasError = !!(result as any).error
        }
      } catch {
        hasError = true
      }
      if (hasError) {
        this.emailErrorHtml =
          'There was a problem sending your email. Please try again.'
        this.emailSendDone = 'n'
      } else {
        this.emailSendDone = 'y'
      }
    } catch (e: any) {
      this.emailErrorHtml =
        'There was a problem sending your email. Please try again.'
    } finally {
      this.emailSending = false
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ancillary-rate-quote-container': AncillaryRateQuoteContainer
  }
}
